import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  POS_HOTKEY_ACTIONS,
  POS_HOTKEY_TECLAS_BLOQUEADAS,
  POS_HOTKEY_TECLAS_PERMITIDAS,
  formatPosHotkeyLabel,
  getPosHotkeyDefaultBindings,
  isPosHotkeyActionConfigurable,
  mergePosHotkeyBindings,
  validatePosHotkeyBindingsDraft,
  type PosHotkeyAccionId,
  type PosHotkeyBindings,
  type PosHotkeyModoImpresionConfig,
  type PosHotkeyTecla,
} from '../../../config/posFacturaHotkeys';
import facturaPosAtajosService from '../../../services/facturaPosAtajosService';
import { getApiErrorMessage } from '../../../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Bindings efectivos actuales; al guardar se refrescan desde API. */
  onSaved: (bindings: PosHotkeyBindings) => void;
  /** Modo de impresión POS del usuario (oculta PDF/ticket según configuración). */
  modoImpresionPos: PosHotkeyModoImpresionConfig;
}

const FacturaPOSAtajosDialog: React.FC<Props> = ({ open, onClose, onSaved, modoImpresionPos }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<PosHotkeyBindings>(() => getPosHotkeyDefaultBindings());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await facturaPosAtajosService.getMine();
      const merged = mergePosHotkeyBindings(res.atajos);
      for (const a of POS_HOTKEY_ACTIONS) {
        if (a.group === 'postEmision' && !isPosHotkeyActionConfigurable(a, modoImpresionPos)) {
          delete merged[a.id];
        }
      }
      setDraft(merged);
    } catch (e) {
      console.error(e);
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar los atajos.'), {
        variant: 'error',
      });
      setDraft(getPosHotkeyDefaultBindings());
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, modoImpresionPos]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const setTecla = (accionId: PosHotkeyAccionId, tecla: PosHotkeyTecla | '') => {
    setDraft((prev) => {
      const next = { ...prev };
      if (!tecla) {
        delete next[accionId];
      } else {
        next[accionId] = tecla;
      }
      return next;
    });
  };

  const handleResetDefaults = () => {
    setDraft(getPosHotkeyDefaultBindings());
  };

  const handleSave = async () => {
    const err = validatePosHotkeyBindingsDraft(draft);
    if (err) {
      enqueueSnackbar(err, { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = POS_HOTKEY_ACTIONS.filter(
        (a) => draft[a.id] && isPosHotkeyActionConfigurable(a, modoImpresionPos),
      ).map((a) => ({
        accionId: a.id,
        tecla: draft[a.id] as string,
      }));
      const res = await facturaPosAtajosService.saveMine(payload);
      const merged = mergePosHotkeyBindings(res.atajos);
      onSaved(merged);
      enqueueSnackbar('Atajos guardados.', { variant: 'success' });
      onClose();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron guardar los atajos.'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const flujo = useMemo(() => POS_HOTKEY_ACTIONS.filter((a) => a.group === 'flujo'), []);
  const cobro = useMemo(() => POS_HOTKEY_ACTIONS.filter((a) => a.group === 'cobro'), []);
  const postEmision = useMemo(
    () =>
      POS_HOTKEY_ACTIONS.filter(
        (a) => a.group === 'postEmision' && isPosHotkeyActionConfigurable(a, modoImpresionPos),
      ),
    [modoImpresionPos],
  );

  /** Tecla → caption de la acción que la usa (para deshabilitar en otros selects). */
  const teclaEnUsoPor = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of POS_HOTKEY_ACTIONS) {
      if (a.group === 'postEmision' && !isPosHotkeyActionConfigurable(a, modoImpresionPos)) {
        continue;
      }
      const tecla = draft[a.id];
      if (tecla) map.set(tecla, a.caption);
    }
    return map;
  }, [draft, modoImpresionPos]);

  const renderRows = (actions: typeof POS_HOTKEY_ACTIONS) =>
    actions.map((a) => (
      <Stack
        key={a.id}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ py: 0.75 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600}>
            {a.caption}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {a.tooltip}
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label="Tecla"
          value={draft[a.id] ?? ''}
          onChange={(e) => setTecla(a.id, (e.target.value || '') as PosHotkeyTecla | '')}
          sx={{ minWidth: 180 }}
          disabled={loading || saving}
        >
          <MenuItem value="">
            <em>Ninguna</em>
          </MenuItem>
          {POS_HOTKEY_TECLAS_PERMITIDAS.map((t) => {
            const usadaPor = teclaEnUsoPor.get(t);
            const ocupadaPorOtra = Boolean(usadaPor && draft[a.id] !== t);
            return (
              <MenuItem key={t} value={t} disabled={ocupadaPorOtra}>
                {formatPosHotkeyLabel(t)}
                {ocupadaPorOtra ? ` · ${usadaPor}` : ''}
              </MenuItem>
            );
          })}
        </TextField>
      </Stack>
    ));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Atajos de teclado — Factura POS</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Configurá tus atajos. No se pueden usar{' '}
          {POS_HOTKEY_TECLAS_BLOQUEADAS.join(', ')} (reservadas del sistema).
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" color="primary" sx={{ mt: 0.5 }}>
              Flujo
            </Typography>
            {renderRows(flujo)}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="primary">
              Cobro
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
              Agregan el saldo pendiente con el primer medio del tipo (tarjeta requiere
              completar marca y autorización en pantalla).
            </Typography>
            {renderRows(cobro)}
            {postEmision.length > 0 ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" color="primary">
                  Post emisión
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  Tras emitir la factura (diálogo «Venta completada»). Imprimir PDF/ticket solo
                  aparece si tenés impresión POS configurada; ticket solo con ticketera EPSON.
                </Typography>
                {renderRows(postEmision)}
              </>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={handleResetDefaults} disabled={loading || saving} sx={{ mr: 'auto' }}>
          Restaurar predeterminados
        </Button>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={loading || saving}>
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FacturaPOSAtajosDialog;
